import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fab,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import { useLocation } from "react-router-dom";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { useAuth } from "../../context/AuthContext";

const SUGGESTIONS = [
  "Apply casual leave tomorrow for personal work",
  "Claim ₹450 cab for client visit today",
  "Raise travel request to Bangalore next Monday–Wednesday",
  "Raise ticket: laptop charger not working",
  "Mark me WFH today",
  "Fill my timesheet for today: 2h on API, 3h on testing",
];

const ACTION_META = {
  timesheet_draft: {
    successPath: "/viewAllTimesheets",
    defaultSuccess: "Timesheet submitted successfully.",
  },
  leave_draft: {
    successPath: "/leave/my",
    defaultSuccess: "Leave request submitted successfully.",
  },
  regularization_draft: {
    successPath: "/AttendanceRegularization",
    defaultSuccess: "Attendance regularization submitted successfully.",
  },
  overtime_draft: {
    successPath: "/overtime",
    defaultSuccess: "Overtime request submitted successfully.",
  },
  wfh_draft: {
    successPath: "/AttendancePortal",
    defaultSuccess: "Marked as Work From Home for today.",
  },
  reimbursement_draft: {
    successPath: "/my-reimbursements",
    defaultSuccess: "Reimbursement claim submitted successfully.",
  },
  travel_draft: {
    successPath: "/travel",
    defaultSuccess: "Travel request submitted successfully.",
  },
  helpdesk_draft: {
    successPath: "/helpdesk",
    defaultSuccess: "Helpdesk ticket raised successfully.",
  },
};

const LEGACY_STORAGE_KEY = "ai:chat:history";

function historyKeyFor(user) {
  const id = user?.employeeid || user?.id || user?._id;
  return id ? `ai:chat:history:${id}` : null;
}

function loadHistory(user) {
  const key = historyKeyFor(user);
  if (!key) return [];
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
}

export default function ChatAssistant() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { pathname } = useLocation();
  const onChatPage =
    pathname.toLowerCase().startsWith("/chatboard") ||
    pathname.toLowerCase().startsWith("/groupchatboard");
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [actionBusyAt, setActionBusyAt] = useState(null);
  const scrollRef = useRef(null);
  const userKey = historyKeyFor(user);

  // One-time cleanup of the old shared history key (was visible across users).
  useEffect(() => {
    try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch (_) { /* ignore */ }
  }, []);

  // Load this login's history whenever the authenticated user changes.
  useEffect(() => {
    setMessages(loadHistory(user));
    setInput("");
    setOpen(false);
  }, [user, userKey]);

  useEffect(() => {
    api.get(endpoints.ai.status).then((res) => setAiAvailable(res.data?.configured)).catch(() => setAiAvailable(false));
  }, []);

  useEffect(() => {
    if (!userKey) return;
    try {
      localStorage.setItem(userKey, JSON.stringify(messages.slice(-30)));
    } catch (_) { /* ignore quota errors */ }
  }, [messages, userKey]);

  useEffect(() => {
    if (!open || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const userMsg = { role: "user", content, at: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);
    try {
      const res = await api.post(endpoints.ai.chat, { message: content, history });
      const reply = res.data?.reply || "(no reply)";
      const source = res.data?.source;
      const intent = res.data?.intent || null;
      const tool = res.data?.tool || null;
      const action = res.data?.action || null;
      setMessages((m) => [...m, { role: "assistant", content: reply, at: Date.now(), source, intent, tool, action }]);
    } catch (err) {
      // Prefer a friendly reply body over technical error msg — never show raw "server error"
      const reply =
        err.response?.data?.reply ||
        err.response?.data?.msg ||
        "Sorry, I can only answer HRMS-related questions. Please ask something about the portal.";
      setMessages((m) => [...m, { role: "assistant", content: reply, at: Date.now(), source: "refuse" }]);
    } finally { setSending(false); }
  };

  const submitActionDraft = async (messageAt, action) => {
    if (!action?.draft || !action?.type || actionBusyAt) return;
    setActionBusyAt(messageAt);
    const meta = ACTION_META[action.type] || {};
    try {
      let res;
      const d = action.draft;
      if (action.type === "timesheet_draft") {
        res = await api.post(endpoints.timesheet.fromChat, { draft: d });
      } else if (action.type === "leave_draft") {
        res = await api.post(endpoints.leave.requests, {
          leaveType: d.leaveType,
          startDate: d.startDate,
          endDate: d.endDate,
          reason: d.reason,
          halfDay: Boolean(d.halfDay),
          halfDaySlot: d.halfDay ? d.halfDaySlot || "first" : undefined,
        });
      } else if (action.type === "regularization_draft") {
        res = await api.post(endpoints.attendanceRegularization.request, {
          attendanceDate: d.attendanceDate,
          requestedCheckInTime: d.requestedCheckInTime,
          requestedCheckOutTime: d.requestedCheckOutTime,
          reason: d.reason,
        });
      } else if (action.type === "overtime_draft") {
        res = await api.post(endpoints.overtime.submit, {
          workDate: d.workDate,
          startTime: d.startTime,
          endTime: d.endTime,
          reason: d.reason,
        });
      } else if (action.type === "wfh_draft") {
        res = await api.post(endpoints.attendanceExt.wfh, {
          reason: d.reason || "Work from home",
        });
      } else if (action.type === "reimbursement_draft") {
        res = await api.post(endpoints.reimbursement.create, {
          purpose: d.purpose,
          fromDate: d.fromDate,
          toDate: d.toDate,
          expenses: d.expenses,
        });
      } else if (action.type === "travel_draft") {
        res = await api.post(endpoints.travel.create, {
          purpose: d.purpose,
          legs: d.legs,
          submit: true,
        });
      } else if (action.type === "helpdesk_draft") {
        res = await api.post(endpoints.helpdesk.createTicket, {
          subject: d.subject,
          description: d.description,
          category: d.category || "other",
          priority: d.priority || "normal",
        });
      } else {
        throw new Error("Unknown action type");
      }

      const successReply =
        res.data?.msg || res.data?.message || meta.defaultSuccess || "Submitted successfully.";
      const path = meta.successPath ? `\n\nYou can review it in **${meta.successPath}**.` : "";
      setMessages((prev) => {
        const updated = prev.map((msg) => (
          msg.at === messageAt
            ? { ...msg, action: { ...msg.action, completed: true } }
            : msg
        ));
        return [
          ...updated,
          {
            role: "assistant",
            content: `${successReply}${path}`,
            at: Date.now(),
            source: action.type.replace("_draft", "-draft"),
            intent: { title: action.label || "Submitted" },
          },
        ];
      });
    } catch (err) {
      const reply =
        err.response?.data?.msg ||
        err.response?.data?.message ||
        "I couldn't submit that request. Please review the draft and try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, at: Date.now(), source: "refuse" },
      ]);
    } finally {
      setActionBusyAt(null);
    }
  };

  const reset = () => {
    setMessages([]);
    if (userKey) {
      try { localStorage.removeItem(userKey); } catch (_) { /* ignore */ }
    }
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <Tooltip title="AI Assistant" placement="left">
          <Fab
            onClick={() => setOpen(true)}
            aria-label="Open AI assistant"
            sx={{
              position: "fixed",
              right: { xs: 16, sm: 24 },
              // Team chat's send button sits in this same bottom-right corner,
              // so on /chatboard the FAB moves to the top-right instead.
              ...(onChatPage
                ? { top: { xs: 72, md: 80 }, bottom: "auto" }
                : { bottom: { xs: 16, sm: 24 }, top: "auto" }),
              zIndex: 1200,
              background: "linear-gradient(135deg, #6C50BF 0%, #A684E8 100%)",
              color: "#fff",
              width: { xs: 52, sm: 58 },
              height: { xs: 52, sm: 58 },
              boxShadow: "0 8px 24px rgba(108,80,191,0.45)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a3fa8 0%, #9b74e0 100%)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.2s",
            }}
          >
            <Badge
              variant="dot"
              overlap="circular"
              color="success"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ "& .MuiBadge-dot": { border: "2px solid #6C50BF" } }}
            >
              <AutoAwesomeOutlinedIcon sx={{ fontSize: { xs: 24, sm: 26 } }} />
            </Badge>
          </Fab>
        </Tooltip>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        scroll="paper"
        aria-labelledby="ai-assistant-title"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: "hidden",
            maxHeight: isMobile ? "100%" : "min(88vh, 720px)",
            m: isMobile ? 0 : 2,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.75,
            position: "relative",
            flexShrink: 0,
            background: "linear-gradient(135deg, #6C50BF 0%, #8568D4 60%, #A684E8 100%)",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 140,
              height: 140,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.08)",
              pointerEvents: "none",
            }}
          />
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: "relative" }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 40, height: 40 }}>
              <AutoAwesomeOutlinedIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id="ai-assistant-title" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
                InfyU HR Assistant
              </Typography>
              <Typography sx={{ fontSize: 11, opacity: 0.92 }}>
                {aiAvailable === null ? "Checking status…" : aiAvailable ? "Online · Powered by Claude Haiku" : "Offline (configure ANTHROPIC_API_KEY)"}
              </Typography>
            </Box>
            <Tooltip title="New chat">
              <IconButton onClick={reset} size="small" sx={{ color: "#fff" }} aria-label="New chat">
                <RestartAltOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                onClick={() => setOpen(false)}
                size="small"
                aria-label="Close AI assistant"
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.15)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            p: 2,
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {messages.length === 0 && (
            <Box sx={{ textAlign: "center", color: "text.secondary", pt: 2 }}>
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 40, color: "primary.main", opacity: 0.5, mb: 1 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                Hi {user?.name?.split(" ")[0] || "there"}
              </Typography>
              <Typography sx={{ fontSize: 12, mb: 2, px: 1 }}>
                Ask me anything about leave, attendance, payroll, or policies. I'll answer from your data where I can — and fall back to AI otherwise.
              </Typography>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 1, mb: 1 }}>
                Try asking
              </Typography>
              <Stack spacing={0.75} alignItems="stretch">
                {SUGGESTIONS.map((s) => (
                  <Box
                    key={s}
                    onClick={() => send(s)}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      fontSize: 12,
                      textAlign: "left",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "primary.main", bgcolor: "#F4EDFA" },
                    }}
                  >
                    {s}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {messages.map((m, i) => (
            <MessageBubble
              key={i}
              message={m}
              onSubmitAction={submitActionDraft}
              actionBusy={actionBusyAt === m.at}
            />
          ))}

          {sending && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: "flex-start", ml: 4, mt: 0.5 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Thinking…</Typography>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about leave, pay, attendance…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={sending}
              multiline
              maxRows={4}
            />
            <IconButton
              color="primary"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              sx={{
                bgcolor: "primary.main",
                color: "#fff",
                flexShrink: 0,
                "&:hover": { bgcolor: "primary.dark" },
                "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "text.disabled" },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
            <InfoOutlinedIcon sx={{ fontSize: 11, color: "text.secondary" }} />
            <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
              Your chat is private. Simple questions answered from your data with no AI cost.
            </Typography>
          </Stack>
        </Box>
      </Dialog>
    </>
  );
}

function MessageBubble({ message, onSubmitAction, actionBusy }) {
  const isUser = message.role === "user";
  const isLocal = message.source === "local" || message.source === "mcp";
  const isLiveAi = message.source === "mcp+ai";
  const isActionDraft = String(message.source || "").endsWith("-draft");
  const intentTitle = message.intent?.title;
  const action = message.action;
  const draft = action?.draft;
  const hasConfirmableDraft = Boolean(draft && action?.type && ACTION_META[action.type]);

  return (
    <Box sx={{ alignSelf: isUser ? "flex-end" : "flex-start", maxWidth: "85%" }}>
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          borderRadius: 2.5,
          bgcolor: isUser ? "primary.main" : message.error ? "#FEE2E2" : "background.paper",
          color: isUser ? "#fff" : message.error ? "#991B1B" : "text.primary",
          border: isUser ? "none" : "1px solid",
          borderColor: message.error ? "#FECACA" : "divider",
          boxShadow: isUser ? "0 2px 8px rgba(108,80,191,0.25)" : "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            "& strong": { fontWeight: 700 },
          }}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
        />
      </Box>
      {!isUser && hasConfirmableDraft && (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>
            Review Draft
          </Typography>
          <DraftSummary type={action.type} draft={draft} />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              onClick={() => onSubmitAction?.(message.at, action)}
              disabled={actionBusy || action.completed}
              sx={{ minWidth: 150, textTransform: "none" }}
            >
              {actionBusy ? <CircularProgress size={16} color="inherit" /> : (action.label || "Confirm")}
            </Button>
            <Typography sx={{ fontSize: 11, color: "text.secondary", alignSelf: "center" }}>
              {action.completed ? "Submitted" : "Confirm and submit"}
            </Typography>
          </Stack>
        </Box>
      )}
      {!isUser && (isLocal || isLiveAi || isActionDraft || intentTitle) && (
        <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5} ml={1}>
          <OfflineBoltOutlinedIcon sx={{ fontSize: 11, color: isLiveAi || isActionDraft ? "#6C50BF" : "#39C645" }} />
          <Typography sx={{ fontSize: 9, color: isLiveAi || isActionDraft ? "#6C50BF" : "#39C645", fontWeight: 700 }}>
            {intentTitle
              ? `${isActionDraft ? "Action" : "Live data"} · ${intentTitle}`
              : isLiveAi
                ? "Live data · AI framed"
                : "Live data · No AI cost"}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

function DraftSummary({ type, draft }) {
  if (!draft) return null;
  if (type === "timesheet_draft") {
    return (
      <>
        <Typography sx={{ fontSize: 12, mb: 0.75 }}>
          Date: <strong>{draft.date}</strong> · Total: <strong>{Number(draft.hoursWorked || 0).toFixed(1)}h</strong>
        </Typography>
        <Stack spacing={0.5}>
          {(draft.taskDetails || []).map((task, idx) => (
            <Typography key={`${task.taskName}-${idx}`} sx={{ fontSize: 12, color: "text.secondary" }}>
              • {task.taskName} — {Number(task.duration).toFixed(1)}h
              {task.projectName ? ` (${task.projectName})` : ""}
            </Typography>
          ))}
        </Stack>
      </>
    );
  }
  if (type === "leave_draft") {
    return (
      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: 12 }}>Type: <strong>{draft.leaveTypeLabel || draft.leaveType}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>
          Date: <strong>{draft.startDate === draft.endDate ? draft.startDate : `${draft.startDate} → ${draft.endDate}`}</strong>
        </Typography>
        {draft.halfDay && (
          <Typography sx={{ fontSize: 12 }}>Half day: <strong>{draft.halfDaySlot || "first"}</strong></Typography>
        )}
        <Typography sx={{ fontSize: 12 }}>Reason: {draft.reason}</Typography>
      </Stack>
    );
  }
  if (type === "regularization_draft") {
    return (
      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: 12 }}>Date: <strong>{draft.attendanceDate}</strong></Typography>
        {draft.requestedCheckInTime && (
          <Typography sx={{ fontSize: 12 }}>Check-in: <strong>{draft.requestedCheckInTime}</strong></Typography>
        )}
        {draft.requestedCheckOutTime && (
          <Typography sx={{ fontSize: 12 }}>Check-out: <strong>{draft.requestedCheckOutTime}</strong></Typography>
        )}
        <Typography sx={{ fontSize: 12 }}>Reason: {draft.reason}</Typography>
      </Stack>
    );
  }
  if (type === "overtime_draft") {
    return (
      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: 12 }}>Date: <strong>{draft.workDate}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>
          Time: <strong>{draft.startTime} – {draft.endTime}</strong>
        </Typography>
        <Typography sx={{ fontSize: 12 }}>Reason: {draft.reason}</Typography>
      </Stack>
    );
  }
  if (type === "wfh_draft") {
    return (
      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: 12 }}>Date: <strong>{draft.date} (today)</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>Reason: {draft.reason}</Typography>
      </Stack>
    );
  }
  if (type === "reimbursement_draft") {
    return (
      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: 12 }}>
          Amount: <strong>₹{Number(draft.amount || 0).toLocaleString("en-IN")}</strong>
        </Typography>
        <Typography sx={{ fontSize: 12 }}>Category: <strong>{draft.categoryLabel}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>Date: <strong>{draft.date}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>Purpose: {draft.purpose}</Typography>
      </Stack>
    );
  }
  if (type === "travel_draft") {
    return (
      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: 12 }}>
          Route: <strong>{draft.fromCity} → {draft.toCity}</strong>
        </Typography>
        <Typography sx={{ fontSize: 12 }}>
          Dates: <strong>{draft.startDate === draft.endDate ? draft.startDate : `${draft.startDate} → ${draft.endDate}`}</strong>
        </Typography>
        <Typography sx={{ fontSize: 12 }}>Mode: <strong>{draft.mode}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>Purpose: {draft.purpose}</Typography>
      </Stack>
    );
  }
  if (type === "helpdesk_draft") {
    return (
      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: 12 }}>Subject: <strong>{draft.subject}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>Category: <strong>{draft.categoryLabel || draft.category}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>Priority: <strong>{draft.priority}</strong></Typography>
        <Typography sx={{ fontSize: 12 }}>Details: {draft.description}</Typography>
      </Stack>
    );
  }
  return null;
}

function renderMarkdown(text) {
  if (!text) return "";
  const escaped = String(text)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|\n)•\s+(.+)/g, "$1&bull; $2");
}
